import ImprvCoinIcon from '@icons/ImprvCoinIcon';
import { ImprvCoinsPackageModel } from '@models/imprvCoins';
import { setupForDark } from '@theme';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

export default function ImprvCoinPackageOption({
  pkg,
  selected,
  showBadge,
  bonusPercent,
  onSelect,
  disabled = false
}: {
  pkg: ImprvCoinsPackageModel;
  selected: boolean;
  showBadge: boolean;
  bonusPercent: number | null;
  onSelect: () => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const priceFormatter = new Intl.NumberFormat('pl-PL', {
    style: 'currency',
    currency: pkg.price.currency
  });

  const handleSelect = () => {
    if (!disabled) onSelect();
  };

  return (
    <div
      role='radio'
      aria-checked={selected}
      aria-disabled={disabled}
      tabIndex={disabled ? -1 : 0}
      data-testid={`imprv-coin-package-${pkg.sku}`}
      onClick={handleSelect}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleSelect();
        }
      }}
      className={classNames(
        'flex flex-row items-center justify-between gap-md rounded-xs border-[1.5px] p-md',
        'focus-visible:outline-none focus-visible:ring-1',
        setupForDark('focus-visible:ring-primary'),
        disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
        selected
          ? classNames(setupForDark('border-yellow-300'), setupForDark('bg-secondary'))
          : classNames(
              setupForDark('border-input-default'),
              !disabled && setupForDark('hover:border-toggle-btn-unselected')
            )
      )}
    >
      <div className='flex min-w-0 flex-col gap-xxs'>
        <div className='flex flex-row items-center gap-xs'>
          <span className={classNames('P3M', setupForDark('text-primary'))}>{pkg.name}</span>
          {showBadge && (
            <span
              className={classNames(
                'LS2M rounded-xxs px-xs py-[2px] uppercase text-black',
                setupForDark('bg-yellow-300')
              )}
            >
              {t('components.buyImprvCoinsModal.badgePopular')}
            </span>
          )}
        </div>
        <span className={classNames('P5R', setupForDark('text-secondary'))}>
          {bonusPercent === null
            ? t('components.buyImprvCoinsModal.starterCaption')
            : t('components.buyImprvCoinsModal.bonus', { percent: bonusPercent })}
        </span>
      </div>
      <div className='flex shrink-0 flex-col items-end gap-xxs'>
        <div className='flex flex-row items-center gap-xs'>
          <span className={classNames('H6 tabular-nums', setupForDark('text-primary'))}>
            {pkg.quantity}
          </span>
          <ImprvCoinIcon size={20} />
        </div>
        <span className={classNames('P4M tabular-nums', setupForDark('text-secondary'))}>
          {priceFormatter.format(pkg.price.amount / 100)}
        </span>
      </div>
    </div>
  );
}
